import {
    settingsSaveButtonText,
    ConfirmErrorDialogSettingsLoadingFailedText,
} from '../strings.locstring.json';
import { options_txp_advisory_changing_settings } from 'owa-txp-option/lib/strings.locstring.json';
import { userVoiceFeedbackDiscardButton } from 'owa-locstrings/lib/strings/uservoicefeedbackdiscardbutton.locstring.json';
import loc from 'owa-localize';

import { observer } from 'mobx-react';
import { PrimaryButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { Dialog, DialogFooter, DialogType } from 'office-ui-fabric-react/lib/Dialog';
import { IDropdownOption, Dropdown } from 'office-ui-fabric-react/lib/Dropdown';
import { Label } from 'office-ui-fabric-react/lib/Label';
import * as React from 'react';
import CardItem from 'txp-data/lib/schema/viewSchema/CardItem';
import {
    TxpEventFlagsV2,
    getDialogTextForTxpFlag,
    getDialogTitleForTxpFlag,
    settingsOptions,
} from 'owa-txp-option/lib/utils/getTxpFlagInfo';
import TxpGriffinSettingsPayload from 'owa-calendar-configuration/lib/store/schema/TxpGriffinSettingsPayload';
import getUserSettingsAction from 'owa-calendar-configuration/lib/services/getUserSettingsAction';
import convertEntityTypeToEventFlag from '../utils/view/convertEntityTypeToEventFlag';
import TxpSettingsEntryPoint from 'owa-calendar-configuration/lib/store/schema/TxpSettingsEntryPoint';
import EntityType from 'txp-data/lib/schema/innerSchema/EntityType';
import TxpSettingsErrorDialog from './TxpSettingsErrorDialog';
import sendChangedTxpSettings from '../utils/actions/sendChangedTxpSettings';
import getStore, { TxpSettingsViewStateStore } from '../store/store';
import TxpSettingsDialogViewStates from '../store/viewSchema/TxpSettingsDialogViewStates';
import {
    setTxpSettingsDialogViewState,
    setTxpSettingsErrorDialogViewState,
} from '../actions/txpSettingsDialogActions';
import { reloadTxpSettings } from 'owa-calendar-configuration';
import updateLocalTxpSettings from 'owa-txp-option/lib/utils/updateLocalTxpSettings';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import getTxpSettings from 'owa-calendar-configuration/lib/utils/getTxpSettings';
import cleanupTxpSettingsDialog from '../utils/view/cleanupTxpSettingsDialog';
import getTxpGriffinSettingsPayloadFromEntitySetting from '../utils/actions/getTxpGriffinSettingsPayloadFromEntitySetting';
import confirm from 'owa-confirm-dialog/lib/actions/confirm';
import { createCalendarPerfDatapoint } from 'owa-shared-analytics';
import { TxpSettingsDataPointNames } from 'owa-calendar-configuration/lib/helpers/TxpConstants';

let styles = require('./TxpSettingsDialog.scss');

export interface TxpSettingsDialogProps {
    cardItem: CardItem;
}

interface SettingsDialogState {
    isShown: boolean;
    error: string;
    txpSettings: TxpGriffinSettingsPayload;
    isTxpErrorDialogShown: boolean;
}

let counter: number = 0;

@observer
export default class TxpSettingsDialog extends React.Component<
    TxpSettingsDialogProps,
    SettingsDialogState
> {
    private discardId = `discard-${counter++}`;
    private settingsOptions: IDropdownOption[];
    private eventType;
    private changedSettings: TxpGriffinSettingsPayload;
    private originalSetting: number;

    constructor(props: any) {
        super(props);
        this.changedSettings = {
            CreateEventsFromEmailAsPrivate: undefined,
            EntityTypeProcessorLevelSettings: {
                FlightReservation: undefined,
                FoodEstablishmentReservation: undefined,
                LodgingReservation: undefined,
                RentalCarReservation: undefined,
                ParcelDelivery: undefined,
                EventReservation: undefined,
                Invoice: undefined,
                ServiceReservation: undefined,
            },
        };
    }

    componentDidMount() {
        this.getUserSettings(
            true /* shouldLogDatapoint */,
            TxpSettingsEntryPoint.CardSettings,
            this.props.cardItem.entityType
        );
    }

    render() {
        const store = getStore();
        const { cardItem } = this.props;
        // Default to auto-add if we cannot fetch txpSettings for any reason
        let selectedValue = 2;
        this.eventType = convertEntityTypeToEventFlag(cardItem.entityType);

        const viewState = store.txpSettingsDialogViewState.viewState;
        const isSaving = viewState === TxpSettingsDialogViewStates.Saving;
        const isErrorDialogShown =
            store.txpSettingsErrorDialogViewState === TxpSettingsDialogViewStates.Shown ||
            store.txpSettingsErrorDialogViewState === TxpSettingsDialogViewStates.Saving;
        const isDisabled = !this.isSettingsChanged() || isSaving;
        selectedValue = this.getSelectedValue(store);
        this.settingsOptions = settingsOptions();

        return isErrorDialogShown ? (
            <TxpSettingsErrorDialog
                entryPoint={TxpSettingsEntryPoint.CardSettings}
                changedSettings={this.changedSettings}
            />
        ) : (
            <Dialog
                isOpen={viewState === TxpSettingsDialogViewStates.Shown}
                type={DialogType.normal}
                onDismiss={this.onDismiss}
                title={getDialogTitleForTxpFlag(this.eventType as TxpEventFlagsV2)}
                modalProps={{
                    isDarkOverlay: true,
                    className: styles.dialogBox,
                }}>
                <div className={styles.dropDownDiv}>
                    <Label>{getDialogTextForTxpFlag(this.eventType as TxpEventFlagsV2)}</Label>
                    <Dropdown
                        selectedKey={selectedValue}
                        options={this.settingsOptions}
                        className={styles.entitySettingDropdown}
                        onChanged={this.updateFlag(this.eventType as TxpEventFlagsV2)}
                    />
                </div>
                <DialogFooter>
                    <div className={styles.dialogFooterButtons}>
                        {isSaving ? (
                            <Spinner size={SpinnerSize.large} className={styles.spinner} />
                        ) : (
                            <div className={styles.spinnerPlaceholder} />
                        )}
                        <PrimaryButton
                            onClick={this.onSave}
                            text={loc(settingsSaveButtonText)}
                            disabled={isDisabled}
                        />
                        <DefaultButton
                            id={this.discardId}
                            onClick={this.onDiscard}
                            text={loc(userVoiceFeedbackDiscardButton)}
                            disabled={isDisabled}
                        />
                    </div>
                </DialogFooter>
                <p className={styles.disclaimer}>{loc(options_txp_advisory_changing_settings)}</p>
            </Dialog>
        );
    }

    /**
     * Function triggered when user clicks discard
     */
    private onDiscard = () => {
        const viewState = getTxpGriffinSettingsPayloadFromEntitySetting(
            this.eventType,
            this.originalSetting
        );

        setTxpSettingsDialogViewState(viewState);
    };

    /**
     * Function triggered when user clicks dismiss
     */
    private onDismiss() {
        setTxpSettingsDialogViewState({ viewState: TxpSettingsDialogViewStates.Hidden });
        cleanupTxpSettingsDialog();
    }

    /**
     * Function triggered when user clicks save
     */
    private onSave = async () => {
        setTxpSettingsDialogViewState({ viewState: TxpSettingsDialogViewStates.Saving });
        const txpSettingsSaveDatapoint = createCalendarPerfDatapoint(
            TxpSettingsDataPointNames.TxpSettingsSaveV2
        );

        sendChangedTxpSettings(this.changedSettings, txpSettingsSaveDatapoint).then(
            isSaveSuccessful => {
                if (!isSaveSuccessful) {
                    setTxpSettingsErrorDialogViewState(TxpSettingsDialogViewStates.Shown);
                } else {
                    cleanupTxpSettingsDialog();
                }

                setTxpSettingsDialogViewState({ viewState: TxpSettingsDialogViewStates.Hidden });
            }
        );
    };

    /**
     * On change handler for the options dropdown
     */
    private updateFlag = (flag: TxpEventFlagsV2) => (option: IDropdownOption, index?: number) => {
        const selectedValue = this.settingsOptions[index].key as number;
        this.changedSettings.CreateEventsFromEmailAsPrivate = getStore().txpSettingsDialogViewState.txpSettings.CreateEventsFromEmailAsPrivate;
        this.changedSettings.EntityTypeProcessorLevelSettings[flag] = selectedValue;
        const viewState = getTxpGriffinSettingsPayloadFromEntitySetting(flag, selectedValue);
        setTxpSettingsDialogViewState(viewState);
    };

    /**
     * Issues the TXP user settings get GWS call and sets the local state
     */
    private async getUserSettings(
        shouldLogDatapoint?: boolean,
        entryPoint?: TxpSettingsEntryPoint,
        entityType?: EntityType
    ) {
        const response = await getUserSettingsAction(shouldLogDatapoint, entryPoint, entityType);
        reloadTxpSettings();
        updateLocalTxpSettings();

        try {
            const settings: TxpGriffinSettingsPayload = await response.json();
            this.originalSetting = settings.EntityTypeProcessorLevelSettings[this.eventType];
            setTxpSettingsDialogViewState({
                txpSettings: settings,
                viewState: TxpSettingsDialogViewStates.Shown,
            });
        } catch (e) {
            confirm(null, loc(ConfirmErrorDialogSettingsLoadingFailedText), null, {
                hideCancelButton: true,
            });
        }
    }

    /**
     * Determines whether or not the setting in the drop down differs from the setting on load
     */
    private isSettingsChanged() {
        const originalTxpSettings = getTxpSettings();
        return (
            getStore().txpSettingsDialogViewState.txpSettings.EntityTypeProcessorLevelSettings[
                this.eventType
            ] !== originalTxpSettings.EntityTypeProcessorLevelSettings[this.eventType]
        );
    }

    /**
     * Gets the value to be selected in the drop down
     */
    private getSelectedValue(store: TxpSettingsViewStateStore) {
        if (
            store.txpSettingsDialogViewState.txpSettings.EntityTypeProcessorLevelSettings[
                this.eventType
            ] !== null
        ) {
            return store.txpSettingsDialogViewState.txpSettings.EntityTypeProcessorLevelSettings[
                this.eventType
            ];
        }
    }
}
